import React from 'react';
import { Mail, Phone, ArrowUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import logoImg from '../assets/logo_transparent.png';

const LinkedinIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" /><rect x="2" y="9" width="4" height="12" /><circle cx="4" cy="4" r="2" /></svg>);
const TwitterIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>);
const InstagramIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5" /><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" /><line x1="17.5" y1="6.5" x2="17.51" y2="6.5" /></svg>);
const GithubIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" /></svg>);

const footerLinks = {
  Services: [
    { label: 'AI & Machine Learning', href: '/#services' },
    { label: 'Mobile App Development', href: '/#services' },
    { label: 'Web Development', href: '/#services' },
    { label: 'Cloud & DevOps', href: '/#services' },
    { label: 'UI/UX Design', href: '/#services' },
    { label: 'Cybersecurity & QA', href: '/#services' },
  ],
  Products: [
    { label: 'Connect Students', href: '/connect-students', isRoute: true },
    { label: 'Pulse CRM', href: '/pulse-crm', isRoute: true },
    { label: 'Namma CET', href: '/namma-cet', isRoute: true },
    { label: 'MindPlan AI', href: '/mindplan-ai', isRoute: true },
    { label: 'QuBot AI', href: '/#products' },
    { label: 'QuTrack', href: '/#products' },
    { label: 'QuLearn', href: '/#products' },
  ],
  Training: [
    { label: 'SDP Programme', href: '/#training' },
    { label: 'FDP Programme', href: '/#training' },
    { label: 'Internships', href: '/#training' },
    { label: 'College Partnerships', href: '/#training' },
  ],
  Company: [
    { label: 'About Us', href: '/about', isRoute: true },
    { label: 'Our Team', href: '/team', isRoute: true },
    { label: 'Careers', href: '/careers', isRoute: true },
    { label: 'Blog', href: '/blog', isRoute: true },
    { label: 'Contact', href: '/#contact' },
  ],
};

const socials = [
  { icon: <LinkedinIcon />, href: '#', label: 'LinkedIn' },
  { icon: <TwitterIcon />, href: '#', label: 'Twitter' },
  { icon: <InstagramIcon />, href: '#', label: 'Instagram' },
  { icon: <GithubIcon />, href: '#', label: 'GitHub' },
];

export default function Footer() {
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <footer className="bg-slate-950 text-white relative overflow-hidden">
      {/* Top gradient bar */}
      <div className="h-1 w-full bg-gradient-to-r from-brand-blue via-brand-accent to-brand-orange" />

      {/* Bg blobs */}
      <div className="absolute top-0 left-0 w-96 h-96 rounded-full bg-brand-blue/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-72 h-72 rounded-full bg-brand-orange/5 blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Main footer body */}
        <div className="py-16 grid lg:grid-cols-6 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="w-64 sm:w-72 h-20 rounded-2xl bg-white p-3 flex items-center justify-center overflow-hidden">
                <img src={logoImg} alt="QuBright" className="w-full h-full object-contain scale-[1.7]" />
              </div>
            </div>

            <p className="font-space text-brand-orange text-sm italic mb-4 font-medium">
              "Where Creativity Meets Precision"
            </p>

            <p className="font-inter text-slate-400 text-sm leading-relaxed mb-6 max-w-xs">
              Delivering cutting-edge software solutions across AI, mobile, web, and cloud —
              empowering businesses and future technologists alike.
            </p>

            {/* Socials */}
            <div className="flex gap-3">
              {socials.map((s, i) => (
                <a
                  key={i}
                  href={s.href}
                  aria-label={s.label}
                  className="w-9 h-9 rounded-lg bg-white/8 border border-white/10 flex items-center justify-center text-slate-400 hover:text-white hover:bg-brand-blue transition-all duration-200"
                >
                  {s.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-syne font-bold text-white text-sm mb-4 uppercase tracking-wider">
                {category}
              </h4>
              <ul className="space-y-2.5">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.isRoute ? (
                      <Link
                        to={link.href}
                        className="font-inter text-slate-400 text-sm hover:text-white transition-colors duration-200 hover:translate-x-1 inline-block transition-transform"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="font-inter text-slate-400 text-sm hover:text-white transition-colors duration-200 hover:translate-x-1 inline-block transition-transform"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Contact strip */}
        <div className="border-t border-white/8 py-6 flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-6">
            <a href="mailto:qubrightoffice@gmail.com" className="flex items-center gap-2 font-inter text-slate-400 text-sm hover:text-white transition-colors">
              <Mail size={14} className="text-brand-blue" />
              qubrightoffice@gmail.com
            </a>
            <a href="tel:+917676654809" className="flex items-center gap-2 font-inter text-slate-400 text-sm hover:text-white transition-colors">
              <Phone size={14} className="text-brand-orange" />
              +91 7676654809
            </a>
          </div>

          {/* Back to top */}
          <button
            onClick={scrollToTop}
            className="w-9 h-9 rounded-full bg-brand-blue/20 border border-brand-blue/30 flex items-center justify-center text-brand-blue hover:bg-brand-blue hover:text-white transition-all duration-200"
            aria-label="Back to top"
          >
            <ArrowUp size={16} />
          </button>
        </div>

        {/* Copyright */}
        <div className="border-t border-white/6 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-inter text-slate-500 text-xs">
            &copy; {new Date().getFullYear()} QuBright Technologies LLP. All rights reserved.
          </p>
          <div className="flex gap-4">
            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item) => (
              <a key={item} href="#" className="font-inter text-slate-500 text-xs hover:text-slate-300 transition-colors">
                {item}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
